import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import PageTitle from "../../../../layouts/PageTitle";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { errorAlert, successAlert, warnAlert } from "../../../../components/alert/defaultAlert";
import { useFormik } from "formik";
import * as Yup from "yup";
import ChatbotSettingsForm from "./ChatbotSettingsForm";
import { convertBooleanToStatus } from "../../../../../utils/ConverterUtils";

const ChatbotSettings = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const [companyUuid, setCompanyUuid] = useState(null);
    const [loading, setLoading] = useState(true);
    const formSchema = Yup.object().shape({
        chatbotApiToken: Yup.string().nullable(),
        model: Yup.string().nullable(),
    });

    const formik = useFormik({
        initialValues: {
            chatbotApiToken: '',
            model: 'gpt-4o-mini',
        },
        validationSchema: formSchema,
        onSubmit: async (values) => {
            try {
                const resolvedChatbotApiToken = values.chatbotApiToken?.trim() || entityData?.chatbot?.apiToken || '';

                if (!resolvedChatbotApiToken) {
                    warnAlert("Informe o token da IA para salvar as configurações do agente.");
                    return;
                }

                const chatbotPayload = {
                    id: entityData?.chatbot?.id,
                    apiToken: resolvedChatbotApiToken,
                    model: values.model,
                    status: convertBooleanToStatus(status)
                };

                if (entityData?.chatbot?.id) {
                    await axiosInstance.patch(`${Api.parameter.chatbot}/${entityData.chatbot.id}`, chatbotPayload);
                } else {
                    await axiosInstance.post(Api.parameter.chatbot, chatbotPayload);
                }

                successAlert(t('message.operation_successfully'));
                loadSettings(); // Recarregar dados
            } catch (error) {
                errorAlert(error);
            }
        }
    });

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const [whatsappResp, chatbotResp, companyResp] = await Promise.all([
                axiosInstance.get(Api.parameter.whatsapp).catch(() => ({ data: [] })),
                axiosInstance.get(Api.parameter.chatbot).catch(() => ({ data: [] })),
                axiosInstance.get(Api.registration.company_info).catch(() => ({ data: null }))
            ]);

            const whatsappData = Array.isArray(whatsappResp.data) 
                ? whatsappResp.data[0] 
                : whatsappResp.data;
            const chatbotData = Array.isArray(chatbotResp.data) 
                ? chatbotResp.data[0] 
                : chatbotResp.data;

            setEntityData({
                whatsapp: whatsappData || null,
                chatbot: chatbotData || null
            });

            // Salvar UUID da empresa para o webhook
            if (companyResp.data?.uuid) {
                setCompanyUuid(companyResp.data.uuid);
            }
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageTitle
                activeMenu={t('chatbot_settings')}
                motherMenu={t('chat_bot')}
            />

            <div className="chatbot-settings-page">
                {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('loading')}...</span>
                            </div>
                            <p className="mt-3 text-muted">{t('loading')}...</p>
                        </div>
                ) : (
                    <div className="chatbot-settings__wizard">
                        <ChatbotSettingsForm
                            formik={formik}
                            onSubmit={formik.handleSubmit}
                            setStatus={setStatus}
                            entityData={entityData}
                            companyUuid={companyUuid}
                            onWhatsAppConnected={async (result) => {
                                if (result.activationComplete) {
                                    successAlert(result.message || t('whatsapp_embedded_signup_success'));
                                }
                                if (result.whatsapp) {
                                    setStatus(true);
                                    setEntityData((current) => ({ ...current, whatsapp: result.whatsapp }));
                                }
                            }}
                            onWhatsAppDisconnected={async (result) => {
                                // Limpa o estado local sempre; o aviso distingue quando a Meta não confirmou o webhook.
                                setEntityData((current) => ({ ...current, whatsapp: null }));
                                setStatus(false);
                                if (result?.metaUnsubscribed === false) {
                                    warnAlert(t('whatsapp_disconnect_meta_warning'));
                                } else {
                                    successAlert(t('whatsapp_disconnect_success'));
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default ChatbotSettings;
